/**
 * Входы в окна native-пакета из карточки ресурса (UA1c п. 7). Карточка вида
 * ресурса не знает: список «Товары», «Поставки», «Касса»… объявляет пакет вида
 * (`resource-onec-ui`, `resource-aqsi-ui`), приложение регистрирует его в
 * composition, а карточка показывает кнопки и открывает окно по его имени
 * (`kind` реестра окон) с rid этого ресурса. Ветвлений по виду в Platform нет.
 *
 * Native-пакет не импортирует `platform-ui`: он объявляет объект той же
 * формы, совместимость проверяет компилятор при регистрации в `apps/web`.
 */
export interface ResourceWindowEntry {
  /** Имя окна в реестре окон (`onec.products`): ресурсное, с `:rid` в адресе. */
  window: string
  /** Подпись кнопки: «Товары», «Касса». */
  label: string
  /**
   * Право вида, без которого окно бесполезно (`catalog.read`). Кнопка только
   * отражает его — окно и сервер проверят сами.
   */
  capability: string
}

export interface ResourceEntriesExtension {
  /** Вид ресурса в реестре Platform: `onec`, `aqsi`. */
  kind: string
  entries: readonly ResourceWindowEntry[]
}

export interface ResourceEntryRegistry {
  /** Входы вида; неизвестный вид — пусто. */
  of: (kind: string) => readonly ResourceWindowEntry[]
}

const NONE: readonly ResourceWindowEntry[] = []

/** Реестр входов: каждый вид — один список; повтор вида или окна — ошибка сборки приложения. */
export function createResourceEntryRegistry(extensions: readonly ResourceEntriesExtension[]): ResourceEntryRegistry {
  const byKind = new Map<string, readonly ResourceWindowEntry[]>()
  for (const extension of extensions) {
    if (extension.kind.trim() === '') throw new Error('Входы в окна без вида ресурса')
    if (byKind.has(extension.kind)) throw new Error(`Входы вида «${extension.kind}» зарегистрированы дважды`)
    const windows = new Set<string>()
    for (const entry of extension.entries) {
      if (windows.has(entry.window)) throw new Error(`Окно «${entry.window}» во входах вида «${extension.kind}» дважды`)
      windows.add(entry.window)
    }
    byKind.set(extension.kind, extension.entries)
  }
  return { of: (kind) => byKind.get(kind) ?? NONE }
}

export const EMPTY_RESOURCE_ENTRIES: ResourceEntryRegistry = createResourceEntryRegistry([])
